import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Heading, Paragraph, Skeleton, Tag } from '@digdir/designsystemet-react';
import type { TeamSurveys } from '../../../api/queries';
import { Routes } from '../../../enums/Routes';
import { buildRoute } from '../../../utils/routeBuilder';
import { DateUtils } from '../../../utils/DateUtils';
import { scoreStatusOf } from '../../../domain/psychological-safety/team-result';
import { SurveyStatusCardUtils } from '../../../utils/SurveyStatusCardUtils/SurveyStatusCardUtils';
import { useGetLatestSurveyScore } from '../../../hooks/queries/useGetLatestSurveyScore';
import {
  changeTone,
  formatChange,
  formatScore,
} from '../../../components/AnalysisLayout/analysis.utils';
import { getCompletedSurveys, type Area } from '../dashboard.utils';
import classes from '../TeamDashboard.module.css';

type LatestResultProps = {
  teamId: number;
  surveys?: TeamSurveys[];
  /** Samme fokusområde som teamet får i handlingsplanen, når områdene har data */
  focusArea?: Area;
  allAreasGood: boolean;
};

const lights = {
  success: 'greenLight',
  warning: 'yellowLight',
  danger: 'redLight',
} as const;

const formatPeriod = (survey: TeamSurveys) => {
  const start = DateUtils.getShortDateFromDate(survey.createdAt, true);
  const deadline = SurveyStatusCardUtils.getDeadlineDate(survey.createdAt, survey.deadlineInDays);
  return deadline ? `${start} – ${DateUtils.getShortDateFromDate(deadline, true)}` : start;
};

/** Resultatet fra siste fullførte måling: lys, hva det er mest å hente på, snitt og svar */
export const LatestResult = ({ teamId, surveys, focusArea, allAreasGood }: LatestResultProps) => {
  const { t } = useTranslation();
  const { data: latestScore, isLoading } = useGetLatestSurveyScore(teamId);
  const { latest, previous } = getCompletedSurveys(surveys);

  if (isLoading) {
    return (
      <section aria-busy="true" aria-label={t('dashboard.latestSurvey.title')} className={classes.statusCard}>
        <Skeleton variant="rectangle" width="100%" height={180} />
      </section>
    );
  }

  const currentScore = latestScore?.currentScore ?? 0;
  const maxScore = latestScore?.maxScore ?? 0;
  const status = maxScore ? scoreStatusOf(currentScore) : 'zero';

  // Without a result the process says where the team is and what to do next, so this card does not repeat it
  if (status !== 'success' && status !== 'warning' && status !== 'danger') return null;

  const light = t(`dashboard.latestSurvey.${lights[status]}`);
  const headline = allAreasGood
    ? t('dashboard.status.allGood')
    : focusArea
      ? t('dashboard.status.mostToGain', {
          area: (focusArea.key ? t(`dashboard.areas.${focusArea.key}.name`) : focusArea.name).toLowerCase(),
        })
      : t('dashboard.status.headlineFallback');
  const change = latest && previous ? currentScore - previous.score : undefined;

  return (
    <section aria-labelledby="latest-result-heading" className={classes.statusCard}>
      <div className={classes.statusText}>
        <Tag data-size="sm" data-color={status}>
          {latest ? `${light} · ${formatPeriod(latest)}` : light}
        </Tag>
        <Heading id="latest-result-heading" level={2} data-size="lg" className={classes.balance}>
          {headline}
        </Heading>
        <Paragraph className={classes.subline}>{t(`dashboard.latestSurvey.${status}Message`)}</Paragraph>
        {latest && (
          <div className={classes.buttons}>
            <Button asChild>
              <Link to={SurveyStatusCardUtils.buildAnalyzeSurveyRoute(teamId, latest.id)}>
                {t('dashboard.status.seeActions')}
              </Link>
            </Button>
            <Button asChild variant="secondary">
              <Link to={buildRoute(Routes.InterpretiveReport, { teamId, surveyId: latest.id })}>
                {t('dashboard.status.seeReport')}
              </Link>
            </Button>
          </div>
        )}
      </div>
      <dl className={classes.statusStats}>
        <div className={classes.stat}>
          <dt>{t('dashboard.status.average')}</dt>
          <dd className={classes.statValue}>
            <span className={classes.bigNumber}>{formatScore(currentScore, 2)}</span>
            <span className={classes.subtle}>{t('analysis.scoreOf')}</span>
          </dd>
          {change !== undefined && (
            <dd className={classes.statNote} data-tone={changeTone(change)}>
              {t('dashboard.status.sinceLast', { change: formatChange(change, 2) })}
            </dd>
          )}
        </div>
        {latest && (
          <div className={classes.stat}>
            <dt>{t('dashboard.status.answered')}</dt>
            <dd className={classes.statValue}>
              <span className={classes.bigNumber}>{latest.numberOfRespondents}</span>
              <span className={classes.subtle}>
                {t('dashboard.status.ofMembers', { count: latest.expectedParticipantsCount })}
              </span>
            </dd>
            <dd className={classes.statNote}>{t('dashboard.status.anonymous')}</dd>
          </div>
        )}
      </dl>
    </section>
  );
};
